import { Log } from "./log";
import {
  Config,
  defaultSupportedChains,
  AACore,
  AAProvider,
  Account,
  Transaction,
  UserOpBundle,
  AccountAbstraction,
  FeeQuotes,
  ChainInfo,
  NetworkAuth,
  Chains,
} from "../types";

abstract class _AccountAbstraction extends AccountAbstraction {
  protected readonly _contractVersion: string = "2.0.0"; //1.0.0
  protected readonly _contractModel: string = "SIMPLE";
}

interface _Config extends Config {
  supportedChainIds: number[];
}

export class GMAccountAbstraction extends _AccountAbstraction {
  private declare readonly _auth: NetworkAuth; // particle_auth.ParticleNetwork.Auth;
  private declare _eoaProvider: AAProvider; // particle_provider.ParticleProvider;
  private declare _aa: AACore; // particle_aa.SmartAccount;
  private declare _aaProvider: AAProvider; // particle_aa.AAWrapProvider;
  private declare _chains: Chains; // particle_chains.chains
  private _injected: boolean = false;
  private declare _account: Account;
  private declare _address: string;
  private declare _ownerAddress: string;

  constructor(auth: NetworkAuth) {
    super();
    this._auth = auth;
  }

  public async connect(config: _Config): Promise<Account> {
    const particle_provider = await import("@particle-network/provider");
    const particle_aa = await import("@particle-network/aa");
    this._eoaProvider = new particle_provider.ParticleProvider(this._auth);

    const particle_chains = await import("@particle-network/chains");
    const { chains } = particle_chains;
    this._chains = chains;

    this._aa = new particle_aa.SmartAccount(this._eoaProvider, {
      projectId: config.projectId,
      clientKey: config.clientKey,
      appId: config.appId,
      aaOptions: {
        accountContracts: {
          [this._contractModel]: [
            {
              version: this._contractVersion,
              chainIds: config.supportedChainIds,
            },
          ],
        },
      },
    });
    this._aa.setSmartAccountContract({
      name: this._contractModel,
      version: this._contractVersion,
    });
    this._aaProvider = new particle_aa.AAWrapProvider(
      this._aa,
      particle_aa.SendTransactionMode.UserSelect
    );
    await this._getBaseConfigs();
    return this.account;
  }

  private async _getBaseConfigs() {
    try {
      await this.getAccount();
      await this.getAddress();
      await this.getOwner();
    } catch (e) {
      Log.e("get base configs error", e);
    }
  }

  private _isGasless(gas?: any) {
    const currentChainId = this._auth.getChainId();

    return (
      [
        defaultSupportedChains.sepolia,
        defaultSupportedChains.gmTestnet,
        defaultSupportedChains.gmMainnet,
      ].includes(currentChainId) && !!gas.verifyingPaymasterGasless
    );
  }

  private _throwError(v: any) {
    throw new Error(v);
  }

  /**
   * *** base config methods ***
   */
  public get core(): AACore {
    return this._aa;
  }
  public get provider(): AAProvider {
    this._injectedRequestTransaction();
    return this._aaProvider;
  }

  public get account(): Account {
    return this._account;
  }
  public get address(): string {
    return this._address;
  }
  public get owner(): string {
    return this._ownerAddress;
  }
  public get chainId(): number {
    return this._auth.getChainId();
  }
  public get chainInfo(): ChainInfo {
    return this._auth.getChain();
  }

  public async getAccount(): Promise<Account> {
    this._account = await this._aa.getAccount();
    return this.account;
  }

  public async getAddress(): Promise<string> {
    this._address = await this._aa.getAddress();
    return this.address;
  }

  public async getOwner(): Promise<string> {
    this._ownerAddress = await this._aa.getOwner();
    return this.owner;
  }

  public setChainId(chainId: number): boolean {
    const info = this._chains.getEVMChainInfoById(chainId);

    if (info == null) {
      Log.e("setChainId error", "Unsupported chain");
      throw new Error("Unsupported chain");
    }

    this._auth.setChainInfo(info);
    return true;
  }

  public getChainInfo(chainId: number): ChainInfo {
    const info = this._chains.getEVMChainInfoById(chainId);

    if (info == null) {
      Log.e("getChainInfo error", "Unsupported chain");
      throw new Error("Unsupported chain");
    }

    return info;
  }

  public async sendTransaction(
    tx: Transaction | Transaction[]
  ): Promise<string> {
    try {
      const feeQuotesResponse = await this._aa.getFeeQuotes(tx);
      Log.d("sendTransaction feeQuotesResponse", feeQuotesResponse);

      let userOp: UserOpBundle["userOp"];
      let userOpHash: string;
      const useGasless = this._isGasless(feeQuotesResponse);
      if (useGasless) {
        const fee = feeQuotesResponse.verifyingPaymasterGasless!;
        userOp = fee.userOp;
        userOpHash = fee.userOpHash;
      } else if (!!feeQuotesResponse.verifyingPaymasterNative) {
        const fee = feeQuotesResponse.verifyingPaymasterNative;
        userOp = fee.userOp;
        userOpHash = fee.userOpHash;
        // const userOpBundle = await this._aa.buildUserOperation({
        //   tx: tx,
        //   // feeQuote: fee.feeQuote,
        // });
        // userOp = userOpBundle.userOp;
        // userOpHash = userOpBundle.userOpHash;
      } else {
        this._throwError("insufficient available gas fee");
        return "";
      }
      Log.d("sendTransaction userOpBundle", { useGasless, userOp, userOpHash });

      const hash = await this._aa.sendTransaction({
        userOp,
        userOpHash,
      });
      // const hash = await this._aa.sendUserOperation({
      //   userOp,
      //   userOpHash,
      // });

      Log.d("sendTransaction hash", hash);

      return hash;
    } catch (e) {
      Log.e("sendTransaction error", e);
      throw e;
    }
  }

  private _injectedRequestTransaction() {
    if (this._injected) return;

    this._injected = true;
    this._aaProvider.once(
      "RequestSendTransaction", //particle_aa.SendTransactionEvent.Request
      (feeQuotesResponse: FeeQuotes) => {
        const useGasless = this._isGasless(feeQuotesResponse);

        Log.d("RequestSendTransaction ", { feeQuotesResponse, useGasless });

        if (useGasless) {
          // send gasless
          this._aaProvider.resolveSendTransaction(
            feeQuotesResponse.verifyingPaymasterGasless
          );
        }
        // else if (
        //   !!feeQuotesResponse?.tokenPaymaster &&
        //   !!feeQuotesResponse?.tokenPaymaster.feeQuotes?.length
        // ) {
        //   todo: coming soon module
        //   // pay gas ERC-20 token
        //   this._aaProvider.resolveSendTransaction({
        //     feeQuote: feeQuotesResponse.tokenPaymaster.feeQuote[0],
        //     tokenPaymasterAddress:
        //       feeQuotesResponse.tokenPaymaster.tokenPaymasterAddress,
        //   });
        // }
        else if (!!feeQuotesResponse?.verifyingPaymasterNative) {
          // pay with native token
          this._aaProvider.resolveSendTransaction(
            feeQuotesResponse.verifyingPaymasterNative
          );
        } else {
          // reject send transaction
          this._aaProvider.rejectSendTransaction({
            message: "insufficient available gas fee",
          });
        }
      }
    );
  }
}
